import { useMemo, useState, type MouseEvent } from 'react';
import { Breadcrumb, Button } from 'antd';
import { ChevronLeft } from 'lucide-react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';

import 'highlight.js/styles/github.css';
import './doc-markdown.css';

interface Props {
  /** 归一化后的文档映射：key 为路径（如 "bridge/004-messages/index.md"），value 为原文。
   *  典型构造方式：
   *  ```ts
   *  const RAW = import.meta.glob('../../content/xxx/index.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;
   *  const docs = Object.fromEntries(Object.entries(RAW).map(([k, v]) => [k.replace(/^.*?content\//, ''), v]));
   *  ```
   */
  docs: Record<string, string>;
  /** 起始文档 key，需存在于 `docs` 中。 */
  docPath: string;
}

/** 通用 markdown 阅读器：
 *  - 外链新标签打开；`#anchor` 保留原生行为；
 *  - 站内相对链接（./ ../）解析后若命中 `docs`，则在组件内切换；
 *  - 维护浏览栈，提供返回 / 面包屑。 */
export default function DocMarkdown({ docs, docPath }: Props) {
  const [stack, setStack] = useState<string[]>([docPath]);
  const current = stack[stack.length - 1];
  const source = docs[current];

  const body = useMemo(() => stripFrontmatter(source ?? ''), [source]);

  const components: Components = useMemo(
    () => ({
      a({ href, children, ...rest }) {
        if (!href) return <>{children}</>;
        if (/^(https?:|mailto:)/i.test(href)) {
          return (
            <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
              {children}
            </a>
          );
        }
        if (href.startsWith('#')) {
          return (
            <a href={href} {...rest}>
              {children}
            </a>
          );
        }
        const target = resolveDocPath(current, href);
        if (target && docs[target]) {
          return (
            <a
              href="#"
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                setStack((s) => [...s, target]);
              }}
              {...rest}
            >
              {children}
            </a>
          );
        }
        return (
          <span className="doc-ref" title={`文档引用：${href}`}>
            {children}
          </span>
        );
      },
    }),
    [current, docs]
  );

  if (!source) {
    return <div className="doc-markdown">文档不存在：{current}</div>;
  }

  return (
    <div className="doc-markdown">
      {stack.length > 1 && (
        <div className="doc-nav">
          <Button
            type="text"
            size="small"
            icon={<ChevronLeft size={14} />}
            onClick={() => setStack((s) => s.slice(0, -1))}
          >
            返回
          </Button>
          <Breadcrumb
            items={stack.map((p, i) => ({
              title:
                i === stack.length - 1 ? (
                  docTitle(docs, p)
                ) : (
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      setStack((s) => s.slice(0, i + 1));
                    }}
                    href="#"
                  >
                    {docTitle(docs, p)}
                  </a>
                ),
            }))}
          />
        </div>
      )}
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeHighlight, { detect: true, ignoreMissing: true }]]}
        components={components}
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}

function docTitle(docs: Record<string, string>, path: string): string {
  const src = docs[path];
  if (src) {
    const m = src.match(/^title:\s*(.+)$/m) || src.match(/^#\s+(.+)$/m);
    if (m) return m[1].trim().replace(/^["']|["']$/g, '');
  }
  return path.split('/').slice(-2, -1)[0] || path;
}

function resolveDocPath(current: string, href: string): string | null {
  const cleaned = href.split('#')[0].split('?')[0];
  if (!cleaned) return null;
  const baseSegs = current.split('/').slice(0, -1);
  const hrefSegs = cleaned.split('/');
  for (const seg of hrefSegs) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (baseSegs.length === 0) return null;
      baseSegs.pop();
      continue;
    }
    baseSegs.push(seg);
  }
  let out = baseSegs.join('/');
  if (cleaned.endsWith('/') || !/\.md$/i.test(out)) {
    out = out.replace(/\/?$/, '/index.md');
  }
  return out;
}

function stripFrontmatter(md: string): string {
  if (!md.startsWith('---')) return md;
  const end = md.indexOf('\n---', 3);
  return end === -1 ? md : md.slice(end + 4).replace(/^\s*\n/, '');
}
